/**
 * adsByTag.ts — results grouped by how the ad was built (hook tactic, offer type). Pure, so the Worker and the tests share it.
 *
 * Rules: an unknown number stays unknown (never 0); a group needs at least MIN_ADS_PER_GROUP different ads before it shows
 * numbers, otherwise it says "Not enough data"; click-through rate is only worked out from rows where both impressions and
 * clicks are known, and `knownRows` says how many rows that is.
 */
import { z } from "zod";
import { AD_HOOK_TACTICS, AD_OFFER_TYPES, type AdConceptTags } from "./adsDrafts.ts";

export const MIN_ADS_PER_GROUP = 3;

export interface ByTagRow {
  creativeId: string;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  tags: Pick<AdConceptTags, "hookTactic" | "offerType">;
}

export const byTagGroupSchema = z.object({
  key: z.string(),
  ads: z.number().int(),
  rows: z.number().int(),
  /** Rows where both impressions and clicks are known: the base of the click-through rate. */
  knownRows: z.number().int(),
  status: z.enum(["ok", "not_enough_data"]),
  impressions: z.number().int().nullable(),
  clicks: z.number().int().nullable(),
  conversions: z.number().int().nullable(),
  /** Clicks divided by impressions, 0–1, from the known rows only. Null when no row has both. */
  ctr: z.number().nullable(),
});
export type ByTagGroup = z.infer<typeof byTagGroupSchema>;

export const byTagResponseSchema = z.object({
  weeks: z.number().int(),
  minAds: z.number().int(),
  hookTactic: z.array(byTagGroupSchema),
  offerType: z.array(byTagGroupSchema),
});
export type ByTagResponse = z.infer<typeof byTagResponseSchema>;

export const byTagQuerySchema = z.object({
  siteId: z.string().min(1).max(100),
  product: z.enum(["sonic_hire", "raya"]).optional(),
  channel: z.enum(["linkedin", "google_search"]).optional(),
  weeks: z.coerce.number().int().min(1).max(52).default(8),
});

const sumKnown = (xs: readonly (number | null)[]): number | null => {
  const known = xs.filter((x): x is number => x !== null);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
};

function group(key: string, rows: readonly ByTagRow[]): ByTagGroup {
  const ads = new Set(rows.map((r) => r.creativeId)).size;
  const both = rows.filter((r) => r.impressions !== null && r.clicks !== null);
  if (ads < MIN_ADS_PER_GROUP) return { key, ads, rows: rows.length, knownRows: both.length, status: "not_enough_data", impressions: null, clicks: null, conversions: null, ctr: null };
  const imp = both.reduce((a, r) => a + r.impressions!, 0);
  const clk = both.reduce((a, r) => a + r.clicks!, 0);
  return {
    key,
    ads,
    rows: rows.length,
    knownRows: both.length,
    status: "ok",
    impressions: sumKnown(rows.map((r) => r.impressions)),
    clicks: sumKnown(rows.map((r) => r.clicks)),
    conversions: sumKnown(rows.map((r) => r.conversions)),
    ctr: both.length > 0 && imp > 0 ? clk / imp : null,
  };
}

export function resultsByTag(rows: readonly ByTagRow[], weeks: number): ByTagResponse {
  return {
    weeks,
    minAds: MIN_ADS_PER_GROUP,
    hookTactic: AD_HOOK_TACTICS.map((k) => group(k, rows.filter((r) => r.tags.hookTactic === k))),
    offerType: AD_OFFER_TYPES.map((k) => group(k, rows.filter((r) => r.tags.offerType === k))),
  };
}
